/**
 * Feature Elements Mapping
 * Map feature codes với các UI element names để phục vụ phân quyền
 * Dựa trên bảng phân quyền SI-50
 * Đồng bộ với database public.features
 */

// Feature codes - khớp với database (từ bảng phân quyền)
export const FEATURE_CODES = {
  // Tính năng phân quyền (có giới hạn theo gói)
  DASHBOARD_CHART: 'dashboard_chart',
  STOCK_SEARCH: 'stock_search',
  STOCK_DETAIL: 'stock_detail',
  STOCK_EVALUATION: 'stock_evaluation',
  QUESTION_SEARCH: 'question_search',
  QUESTION_RESULT: 'question_result',
  WATCHLIST_SYMBOLS: 'watchlist_symbols',
  WATCHLIST_FILTER_PERSONA: 'watchlist_filter_persona',
  WATCHLIST_SAVE_PERSONA: 'watchlist_save_persona',
  AI_COMMENT: 'AI_COMMENT', // AI-generated comments (Pro/Plus only)
  NOTE_AI: 'NOTE_AI', // AI note block generation (credit-based access)

  // Tính năng không phân quyền (public - tất cả users)
  AUTH: 'auth',
  USER_SETTINGS: 'user_settings',
  PAYMENT_HISTORY: 'payment_history',
  COMPARE_PLANS: 'compare_plans',
};

// Feature types
export const FEATURE_TYPES = {
  UI: 'UI',
  API: 'API',
  BOTH: 'BOTH',
};

// UI Element names mapping theo feature code - khớp với database features.metadata.ui_elements
export const FEATURE_UI_ELEMENTS = {
  // Dashboard Chart feature
  [FEATURE_CODES.DASHBOARD_CHART]: [
    'FintechDashboard',
    'SubHeader',
    'StatsGrid',
    'StockTable',
    'MarketOverview',
  ],

  // Stock Search feature
  [FEATURE_CODES.STOCK_SEARCH]: [
    'SearchStock',
    'SearchInput',
    'SearchSubHeader',
    'QuickActions',
  ],

  // Stock Detail feature
  [FEATURE_CODES.STOCK_DETAIL]: [
    'StockDetail',
    'StockHeader',
    'StockChart',
    'DetailTabs',
  ],

  // Stock Evaluation feature
  [FEATURE_CODES.STOCK_EVALUATION]: [
    'CanslimPanel',
    'CompositeScoreCard',
    'FinancialFactsTable',
  ],

  // Question Search feature
  [FEATURE_CODES.QUESTION_SEARCH]: [
    'FilterPanel',
    'QuickQuestions',
  ],

  // Question Result feature
  [FEATURE_CODES.QUESTION_RESULT]: [
    'BreakoutTable',
    'Pagination',
  ],

  // Watchlist Symbols feature
  [FEATURE_CODES.WATCHLIST_SYMBOLS]: [
    'WatchlistNotes',
    'WatchlistSidebar',
    'SymbolsTable',
    'NotesPanel',
    'CreateListModal',
  ],

  // Watchlist Filter Persona feature
  [FEATURE_CODES.WATCHLIST_FILTER_PERSONA]: [
    'LensFilter',
  ],

  // Watchlist Save Persona feature
  [FEATURE_CODES.WATCHLIST_SAVE_PERSONA]: [
    'LensFilter',
  ],

  // Auth feature (public)
  [FEATURE_CODES.AUTH]: [
    'AuthLayout',
    'AuthHeader',
    'AuthDivider',
    'AuthLinks',
    'SocialLogin',
    'ErrorMessage',
    'LoginForm',
    'RegisterForm',
  ],

  // User Settings feature (public - không phân quyền)
  [FEATURE_CODES.USER_SETTINGS]: [
    'UserSettings',
  ],
  
  // Payment History feature (public - không phân quyền)
  [FEATURE_CODES.PAYMENT_HISTORY]: [
    'PaymentHistory',
  ],

  // Compare Plans feature (public)
  [FEATURE_CODES.COMPARE_PLANS]: [
    'ComparePlans',
    'PlansHeader',
    'PlansTable',
    'FAQSection',
    'PlansFooter',
  ],

  // AI Comment feature (Pro/Plus only)
  [FEATURE_CODES.AI_COMMENT]: [
    'AICommentPanel',
    'AIMarketComment',
    'AICommentButton',
    'AIInsightCard',
  ],

  // Note AI feature (credit-based)
  [FEATURE_CODES.NOTE_AI]: [
    'NoteAIPanel',
    'NoteAISummary',
    'NoteAICard',
  ],
};

// Public UI elements (không cần phân quyền - tất cả users đều có quyền)
export const PUBLIC_UI_ELEMENTS = [
  // Auth elements
  ...FEATURE_UI_ELEMENTS[FEATURE_CODES.AUTH],
  // User Settings elements
  ...FEATURE_UI_ELEMENTS[FEATURE_CODES.USER_SETTINGS],
  // Payment History elements
  ...FEATURE_UI_ELEMENTS[FEATURE_CODES.PAYMENT_HISTORY],
  // Compare Plans elements
  ...FEATURE_UI_ELEMENTS[FEATURE_CODES.COMPARE_PLANS],
];

// Public features (không phân quyền)
export const PUBLIC_FEATURES = [
  FEATURE_CODES.AUTH,
  FEATURE_CODES.USER_SETTINGS,
  FEATURE_CODES.PAYMENT_HISTORY,
  FEATURE_CODES.COMPARE_PLANS,
];

// Helper: Lấy feature code từ element name
export const getFeatureByElement = (elementName) => {
  for (const [featureCode, elements] of Object.entries(FEATURE_UI_ELEMENTS)) {
    if (elements.includes(elementName)) {
      return featureCode;
    }
  }
  return null;
};

// Helper: Kiểm tra element có thuộc feature nào không (trừ public elements)
export const isProtectedElement = (elementName) => {
  if (PUBLIC_UI_ELEMENTS.includes(elementName)) {
    return false;
  }
  return getFeatureByElement(elementName) !== null;
};

// Helper: Lấy tất cả UI elements của một feature
export const getElementsByFeature = (featureCode) => {
  return FEATURE_UI_ELEMENTS[featureCode] || [];
};

// Helper: Kiểm tra user có quyền xem element không
export const canAccessElement = (elementName, userFeatures = []) => {
  // Public elements luôn accessible
  if (PUBLIC_UI_ELEMENTS.includes(elementName)) {
    return true;
  }

  // Tìm feature chứa element này
  const featureCode = getFeatureByElement(elementName);
  if (!featureCode) {
    return true; // Element không trong danh sách bảo vệ
  }

  // Kiểm tra user có feature này không (case-insensitive)
  const normalized = featureCode.toUpperCase();
  return userFeatures.some((f) => f.code?.toUpperCase() === normalized);
};

// Helper: Kiểm tra feature có phải public không
export const isPublicFeature = (featureCode) => {
  return PUBLIC_FEATURES.includes(featureCode);
};
